import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Fade, Slide, Zoom } from "react-awesome-reveal";
import {
  FaCalendarAlt,
  FaChevronRight,
  FaClock,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaRegCalendarCheck,
  FaTimes,
  FaTrophy,
} from "react-icons/fa";
import { GiKimono } from "react-icons/gi";

import { getAdherentsByUser } from "../../services/adherentServices";
import {
  getEventsByAdherent,
  getDemoHomeEvents,
} from "../../services/eventServices";

const MAX_EVENTS_HOME = 3;

const EVENT_STYLES = [
  {
    gradient: "from-sky-600 via-blue-700 to-slate-950",
    soft: "from-sky-500/15 via-blue-500/10 to-transparent",
    icon: FaTrophy,
    chip: "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200",
    accent: "text-sky-600 dark:text-sky-300",
  },
  {
    gradient: "from-orange-500 via-amber-600 to-slate-950",
    soft: "from-orange-500/15 via-amber-500/10 to-transparent",
    icon: GiKimono,
    chip: "bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200",
    accent: "text-orange-500 dark:text-orange-300",
  },
  {
    gradient: "from-emerald-600 via-teal-700 to-slate-950",
    soft: "from-emerald-500/15 via-teal-500/10 to-transparent",
    icon: FaRegCalendarCheck,
    chip: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200",
    accent: "text-emerald-600 dark:text-emerald-300",
  },
];

const normalizeEventsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Events)) return response.Events;
  if (Array.isArray(response?.events)) return response.events;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.Events)) return response.data.Events;
  if (Array.isArray(response?.data?.events)) return response.data.events;
  return [];
};

const normalizeAdherentsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.adherents)) return response.adherents;
  if (Array.isArray(response?.Adherents)) return response.Adherents;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.adherents)) return response.data.adherents;
  return [];
};

const getEventStart = (event) =>
  event?.start || event?.date_event || event?.date;

const getEventLocation = (event) =>
  event?.lieu_event || event?.lieu || "Lieu non précisé";

const getEventSchedule = (event) =>
  event?.horaire_event || event?.horaires_event || "";

const getEventCover = (event) =>
  event?.photo_url ||
  event?.image_url ||
  event?.photo ||
  event?.cover_url ||
  "/img/cover/cover_calendar.jpeg";

const cleanText = (value) => {
  if (!value) return "";

  return String(value)
    .replace(/<[^>]*>/g, "")
    .trim();
};

const hasRichText = (value) => {
  if (!value) return false;
  return String(value).trim().length > 0;
};

const isValidDate = (dateValue) => {
  const date = new Date(dateValue);
  return Number.isFinite(date.getTime());
};

const formatDay = (dateValue) => {
  if (!isValidDate(dateValue)) return "--";

  return new Date(dateValue).toLocaleDateString("fr-FR", {
    day: "2-digit",
  });
};

const formatMonth = (dateValue) => {
  if (!isValidDate(dateValue)) return "---";

  return new Date(dateValue).toLocaleDateString("fr-FR", {
    month: "short",
  });
};

const formatFullDate = (dateValue) => {
  if (!isValidDate(dateValue)) return "Date non précisée";

  return new Date(dateValue).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const getAdherentOptionLabel = (adherent) => {
  if (!adherent?.prenom && !adherent?.nom) {
    return `Adhérent #${adherent?.id || ""}`;
  }

  return [adherent.prenom, adherent.nom].filter(Boolean).join(" ");
};

const getAdherentLabel = (event) => {
  const adherent = event?._homeAdherent;

  if (!adherent?.prenom && !adherent?.nom) return "";

  return [adherent.prenom, adherent.nom].filter(Boolean).join(" ");
};

const getUpcomingEvents = (events) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return [...events]
    .filter((event) => {
      const date = new Date(getEventStart(event));
      return Number.isFinite(date.getTime()) && date >= today;
    })
    .sort((a, b) => new Date(getEventStart(a)) - new Date(getEventStart(b)))
    .slice(0, MAX_EVENTS_HOME);
};

const dedupeEvents = (events) => {
  const map = new Map();

  events.forEach((event) => {
    const key = event?.id || `${event?.titre}-${getEventStart(event)}`;

    if (key && !map.has(key)) {
      map.set(key, event);
    }
  });

  return Array.from(map.values());
};

function AgendaSkeleton() {
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20"
        >
          <div className="h-52 animate-pulse rounded-[1.5rem] bg-slate-200 dark:bg-white/10" />

          <div className="space-y-3 p-3 pt-5">
            <div className="h-4 w-24 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
            <div className="h-5 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
            <div className="h-4 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
            <div className="h-11 w-full animate-pulse rounded-2xl bg-slate-200 dark:bg-white/10" />
          </div>
        </div>
      ))}
    </div>
  );
}

function AdherentSelect({
  isConnected,
  adherents,
  selectedAdherentId,
  onChange,
}) {
  if (!isConnected || adherents.length <= 1) return null;

  return (
    <Fade direction="up" triggerOnce delay={180}>
      <div className="rounded-[1.4rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-white/[0.06]">
        <label
          htmlFor="agenda-adherent"
          className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400"
        >
          Judoka affiché
        </label>

        <select
          id="agenda-adherent"
          value={selectedAdherentId}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-slate-950 dark:text-white"
        >
          {adherents.map((adherent) => (
            <option key={adherent.id} value={String(adherent.id)}>
              {getAdherentOptionLabel(adherent)}
            </option>
          ))}
        </select>
      </div>
    </Fade>
  );
}

function AgendaEmptyState({ isConnected, onAction, hasAdherents }) {
  return (
    <Fade direction="up" triggerOnce>
      <div className="overflow-hidden rounded-[2.2rem] border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <div className="grid gap-5 rounded-[1.8rem] bg-slate-50 p-4 dark:bg-white/[0.04] md:grid-cols-[0.85fr_1.15fr] md:items-center md:p-5">
          <div className="relative min-h-64 overflow-hidden rounded-[1.55rem] bg-slate-900">
            <img
              src="/img/cover/cover_calendar.jpeg"
              alt="Agenda Judo Coach Pro"
              className="absolute inset-0 h-full w-full object-cover opacity-80 transition duration-700 hover:scale-105"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/35 to-transparent" />

            <div className="absolute bottom-4 left-4 right-4 text-white">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] backdrop-blur-xl">
                <FaCalendarAlt />
                Agenda
              </div>

              <p className="mt-3 font-heading text-2xl font-bold leading-tight">
                {isConnected
                  ? hasAdherents
                    ? "La saison de ce judoka apparaîtra ici"
                    : "Aucun judoka associé au compte"
                  : "Aucun rendez-vous de démonstration"}
              </p>
            </div>
          </div>

          <div className="px-1 py-2 md:px-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-600 dark:text-sky-300">
              {isConnected ? "Agenda vide" : "Compte démo"}
            </p>

            <h3 className="mt-3 font-heading text-2xl font-bold leading-tight text-slate-950 dark:text-white sm:text-3xl">
              {isConnected
                ? hasAdherents
                  ? "Les prochains événements liés à ce judoka seront affichés ici."
                  : "Ajoutez d’abord un adhérent pour suivre son agenda."
                : "Ajoutez quelques événements au compte démo pour valoriser cette section."}
            </h3>

            <p className="mt-4 max-w-xl text-sm font-medium leading-7 text-slate-600 dark:text-white/55 sm:text-base">
              Compétitions, stages, passages de grade ou rendez-vous club : tout
              remonte automatiquement dans l’agenda.
            </p>

            <button
              type="button"
              onClick={onAction}
              className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-sky-600 to-blue-800 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-sky-600/20 transition hover:-translate-y-0.5 active:translate-y-0 sm:w-auto font-ui"
            >
              {isConnected ? "Voir mon agenda" : "Créer mon compte"}
              <FaChevronRight className="text-xs" />
            </button>
          </div>
        </div>
      </div>
    </Fade>
  );
}

function EventCard({ event, index, onDetails }) {
  const startDate = getEventStart(event);
  const location = getEventLocation(event);
  const schedule = getEventSchedule(event);
  const style = EVENT_STYLES[index % EVENT_STYLES.length];
  const Icon = style.icon;
  const description = cleanText(event?.description);
  const adherentLabel = getAdherentLabel(event);

  return (
    <Zoom triggerOnce delay={index * 80}>
      <article className="group relative h-full overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/60 transition duration-300 hover:-translate-y-1 hover:shadow-2xl dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-br ${style.soft}`}
        />

        <div className="relative h-52 overflow-hidden rounded-[1.55rem] bg-slate-900">
          <img
            src={getEventCover(event)}
            alt={event?.titre || "Événement Judo Coach Pro"}
            className="h-full w-full object-cover opacity-85 transition duration-700 group-hover:scale-105"
            loading="lazy"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/25 to-transparent" />

          <div className="absolute left-4 top-4 overflow-hidden rounded-2xl bg-white text-center shadow-lg dark:bg-slate-950">
            <div
              className={`bg-gradient-to-br ${style.gradient} px-4 py-2 text-white`}
            >
              <p className="text-2xl font-bold leading-none">
                {formatDay(startDate)}
              </p>

              <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em]">
                {formatMonth(startDate)}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => onDetails(event)}
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-2xl border border-white/20 bg-white/15 text-white backdrop-blur-xl transition hover:bg-white hover:text-slate-950 active:scale-95"
            aria-label="Voir les détails de l'événement"
            title="Voir les détails"
          >
            <FaInfoCircle />
          </button>

          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3 text-white">
            <div className="inline-flex max-w-full items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold backdrop-blur-xl">
              <Icon className="shrink-0" />
              <span className="truncate">
                {event?.type_event || "Événement"}
              </span>
            </div>
          </div>
        </div>

        <div className="relative p-3 pt-5">
          <div className="flex flex-wrap gap-2">
            {event?.cate_event && (
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ${style.chip}`}
              >
                {event.cate_event}
              </span>
            )}

            {event?.level_event && (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-white/60">
                {event.level_event}
              </span>
            )}
          </div>

          <h3 className="mt-4 line-clamp-2 font-heading text-xl font-bold leading-tight text-slate-950 dark:text-white">
            {event?.titre || "Événement sans titre"}
          </h3>

          {adherentLabel && (
            <p className="mt-2 text-sm font-bold text-sky-700 dark:text-sky-200">
              {adherentLabel}
            </p>
          )}

          <div className="mt-4 space-y-2 text-sm font-medium text-slate-600 dark:text-white/55">
            <p className="flex items-center gap-2">
              <FaCalendarAlt className={`shrink-0 ${style.accent}`} />
              <span className="line-clamp-1">{formatFullDate(startDate)}</span>
            </p>

            <p className="flex items-center gap-2">
              <FaMapMarkerAlt className="shrink-0 text-orange-500" />
              <span className="line-clamp-1">{location}</span>
            </p>
          </div>

          {hasRichText(schedule) && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/[0.05]">
              <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-white/35">
                <FaClock />
                Horaires
              </p>

              <div
                className="line-clamp-3 text-sm font-medium leading-6 text-slate-600 dark:text-white/55 [&_a]:font-bold [&_a]:text-sky-600 [&_br]:block [&_p]:mb-1 [&_strong]:font-bold"
                dangerouslySetInnerHTML={{ __html: schedule }}
              />
            </div>
          )}

          {description && (
            <p className="mt-4 line-clamp-2 text-sm font-medium leading-6 text-slate-500 dark:text-white/45">
              {description}
            </p>
          )}

          <button
            type="button"
            onClick={() => onDetails(event)}
            className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 dark:border-white/10 dark:bg-white/[0.07] dark:text-white dark:hover:bg-white/10"
          >
            Voir le détail
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      </article>
    </Zoom>
  );
}

function isHtmlContent(value) {
  if (!value) return false;
  return /<\/?[a-z][\s\S]*>/i.test(String(value));
}

function RichContent({ value, emptyText = "Non précisé." }) {
  const content = String(value || "").trim();

  if (!content) {
    return (
      <p className="text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
        {emptyText}
      </p>
    );
  }

  const className =
    "text-sm font-medium leading-7 text-slate-600 break-words [overflow-wrap:anywhere] dark:text-white/60 " +
    "[&_a]:font-bold [&_a]:text-sky-700 dark:[&_a]:text-sky-200 " +
    "[&_br]:block [&_em]:italic [&_strong]:font-bold " +
    "[&_p]:mb-3 [&_ul]:ml-5 [&_ul]:list-disc [&_ol]:ml-5 [&_ol]:list-decimal [&_li]:mb-1 " +
    "[&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-xl " +
    "[&_iframe]:max-w-full [&_iframe]:rounded-xl " +
    "[&_table]:w-full [&_td]:border [&_td]:border-slate-200 [&_td]:p-2 dark:[&_td]:border-white/10";

  if (isHtmlContent(content)) {
    return (
      <div
        className={className}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  return <p className={`${className} whitespace-pre-wrap`}>{content}</p>;
}

function DetailTile({ label, value, className = "" }) {
  return (
    <div
      className={`rounded-2xl bg-slate-50 p-4 dark:bg-white/[0.05] ${className}`}
    >
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </p>

      <p className="mt-2 break-words text-sm font-bold leading-6 text-slate-950 [overflow-wrap:anywhere] dark:text-white sm:text-base">
        {value || "Non précisé"}
      </p>
    </div>
  );
}

function EventDetailsModal({ event, onClose, onAgendaClick }) {
  if (!event) return null;

  const startDate = getEventStart(event);
  const location = getEventLocation(event);
  const schedule = getEventSchedule(event);
  const adherentLabel = getAdherentLabel(event);

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/75 p-2 backdrop-blur-sm sm:items-center sm:p-6">
      <div className="absolute inset-0" onClick={onClose} />

      <Fade direction="up" duration={250} triggerOnce>
        <div className="relative flex max-h-[calc(100dvh-1rem)] w-full max-w-3xl flex-col overflow-hidden rounded-t-[2rem] border border-white/10 bg-white shadow-2xl dark:bg-[#0b1220] sm:max-h-[calc(100dvh-3rem)] sm:rounded-[2rem]">
          <div className="relative h-44 shrink-0 overflow-hidden bg-slate-900 sm:h-60">
            <img
              src={getEventCover(event)}
              alt={event?.titre || "Événement Judo Coach Pro"}
              className="h-full w-full object-cover opacity-75"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/35 to-transparent" />

            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white hover:text-slate-950 active:scale-95"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>

            <div className="absolute bottom-4 left-4 right-4 text-white sm:bottom-5 sm:left-5 sm:right-5">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-200">
                Détail de l’événement
              </p>

              <h2 className="mt-2 line-clamp-2 font-heading text-2xl font-bold leading-tight sm:text-3xl">
                {event?.titre || "Événement sans titre"}
              </h2>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">
            <div className="rounded-[1.6rem] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03] sm:p-5">
              <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-200">
                <FaInfoCircle />
                Description
              </p>

              <div className="overflow-x-auto">
                <RichContent
                  value={event?.description}
                  emptyText="Aucune description disponible."
                />
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <DetailTile
                label="Titre complet"
                value={event?.titre || "Événement sans titre"}
                className="sm:col-span-2"
              />

              <DetailTile label="Date" value={formatFullDate(startDate)} />

              <DetailTile label="Lieu" value={location} />

              <DetailTile label="Type" value={event?.type_event || "Événement"} />

              <DetailTile
                label="Catégorie"
                value={event?.cate_event || "Non précisée"}
              />

              <DetailTile
                label="Niveau"
                value={event?.level_event || "Non précisé"}
              />

              {adherentLabel && (
                <DetailTile label="Judoka" value={adherentLabel} />
              )}
            </div>

            {hasRichText(schedule) && (
              <div className="mt-5 rounded-[1.5rem] border border-orange-200 bg-orange-50 p-4 dark:border-orange-400/20 dark:bg-orange-500/10 sm:p-5">
                <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:text-orange-200">
                  <FaClock />
                  Horaires
                </p>

                <div className="overflow-x-auto">
                  <RichContent
                    value={schedule}
                    emptyText="Aucun horaire renseigné."
                  />
                </div>
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-[#0b1220] sm:p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:bg-white/10"
              >
                Fermer
              </button>

              <button
                type="button"
                onClick={onAgendaClick}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-400 px-5 py-2 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 active:translate-y-0"
              >
                Voir l’agenda complet
                <FaChevronRight className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </Fade>
    </div>
  );
}

function AgendaHome({ isConnected }) {
  const navigate = useNavigate();

  const [managedAdherents, setManagedAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState("");
  const [adherentsLoaded, setAdherentsLoaded] = useState(!isConnected);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const selectedAdherent = useMemo(() => {
    if (!selectedAdherentId) return null;

    return (
      managedAdherents.find(
        (adherent) => String(adherent.id) === String(selectedAdherentId),
      ) || null
    );
  }, [managedAdherents, selectedAdherentId]);

  const sectionContent = useMemo(() => {
    if (isConnected) {
      return {
        eyebrow: "Mon agenda",
        title: "Les rendez-vous du judoka.",
        subtitle:
          "Ce composant affiche le premier judoka géré. Vous pouvez basculer sur un autre profil.",
        note: null,
        primaryLabel: "Voir tout mon agenda",
        primaryPath: "/events-judoka",
      };
    }

    return {
      eyebrow: "Agenda connecté",
      title: "Une saison claire, des événements bien suivis.",
      subtitle:
        "Judo Coach Pro met en avant les prochains rendez-vous pour aider les judokas et les coachs à mieux préparer leur saison.",
      note: "Démonstration : les événements ci-dessous proviennent du compte de démonstration.",
      primaryLabel: "Créer mon compte gratuit",
      primaryPath: "/register",
    };
  }, [isConnected]);

  const handleAgendaClick = () => {
    setSelectedEvent(null);
    navigate(sectionContent.primaryPath);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchManagedAdherents = async () => {
      if (!isConnected) {
        if (isMounted) {
          setManagedAdherents([]);
          setSelectedAdherentId("");
          setAdherentsLoaded(true);
        }

        return;
      }

      setAdherentsLoaded(false);

      try {
        const response = await getAdherentsByUser();
        const adherents = normalizeAdherentsResponse(response);

        if (!isMounted) return;

        setManagedAdherents(adherents);

        setSelectedAdherentId((currentId) => {
          const currentStillExists = adherents.some(
            (adherent) => String(adherent.id) === String(currentId),
          );

          if (currentId && currentStillExists) {
            return currentId;
          }

          return adherents[0]?.id ? String(adherents[0].id) : "";
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des adhérents :", error);

        if (isMounted) {
          setManagedAdherents([]);
          setSelectedAdherentId("");
        }
      } finally {
        if (isMounted) {
          setAdherentsLoaded(true);
        }
      }
    };

    fetchManagedAdherents();

    return () => {
      isMounted = false;
    };
  }, [isConnected]);

  useEffect(() => {
    let isMounted = true;

    const fetchSelectedAdherentEvents = async () => {
      if (!selectedAdherentId) return [];

      const response = await getEventsByAdherent(selectedAdherentId);
      const events = normalizeEventsResponse(response);

      return dedupeEvents(
        events.map((event) => ({
          ...event,
          _homeAdherent: selectedAdherent
            ? {
                id: selectedAdherent.id,
                nom: selectedAdherent.nom,
                prenom: selectedAdherent.prenom,
              }
            : null,
        })),
      );
    };

    const fetchVisitorEvents = async () => {
      const response = await getDemoHomeEvents();
      return normalizeEventsResponse(response);
    };

    const fetchEvents = async () => {
      if (isConnected && !adherentsLoaded) {
        setLoading(true);
        return;
      }

      if (isConnected && !selectedAdherentId) {
        setEvents([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const loadedEvents = isConnected
          ? await fetchSelectedAdherentEvents()
          : await fetchVisitorEvents();

        if (isMounted) {
          setEvents(getUpcomingEvents(loadedEvents));
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des événements :", error);

        if (isMounted) {
          setEvents([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchEvents();

    return () => {
      isMounted = false;
    };
  }, [isConnected, adherentsLoaded, selectedAdherentId, selectedAdherent]);

  const hasEvents = events.length > 0;
  const hasAdherents = managedAdherents.length > 0;

  return (
    <section className="relative overflow-hidden bg-slate-50 px-4 py-16 text-slate-950 dark:bg-[#07111f] dark:text-white sm:px-6 sm:py-20 lg:px-8">
      <div className="absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full bg-sky-500/10 blur-3xl dark:bg-sky-400/10" />
      <div className="absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-orange-500/10 blur-3xl" />
      <div className="absolute -left-24 bottom-24 h-72 w-72 rounded-full bg-blue-700/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        <div className="mb-9 grid gap-6 lg:grid-cols-[0.68fr_0.32fr] lg:items-end">
          <Slide direction="left" triggerOnce>
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] text-sky-700 shadow-sm dark:border-sky-400/20 dark:bg-white/[0.06] dark:text-sky-200">
                <FaCalendarAlt />
                {sectionContent.eyebrow}
              </div>

              <h2 className="mt-5 font-heading text-3xl font-bold leading-tight text-slate-950 dark:text-white sm:text-4xl lg:text-5xl">
                {sectionContent.title}
              </h2>

              <p className="mt-4 max-w-2xl text-base font-medium leading-8 text-slate-600 dark:text-white/55 sm:text-lg">
                {sectionContent.subtitle}
              </p>
            </div>
          </Slide>

          <div className="grid gap-3">
            <AdherentSelect
              isConnected={isConnected}
              adherents={managedAdherents}
              selectedAdherentId={selectedAdherentId}
              onChange={setSelectedAdherentId}
            />

            <Fade direction="up" triggerOnce delay={120}>
              <div className="flex lg:justify-end">
                <button
                  type="button"
                  onClick={handleAgendaClick}
                  className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-400 px-6 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 active:translate-y-0 sm:w-auto lg:w-full font-ui"
                >
                  {sectionContent.primaryLabel}
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            </Fade>
          </div>
        </div>

        {sectionContent.note && (
          <Fade direction="up" triggerOnce>
            <div className="mb-6 rounded-[1.5rem] border border-orange-200 bg-orange-50 p-4 text-sm font-semibold leading-6 text-orange-800 dark:border-orange-400/20 dark:bg-orange-500/10 dark:text-orange-100">
              {sectionContent.note}
            </div>
          </Fade>
        )}

        {loading && <AgendaSkeleton />}

        {!loading && !hasEvents && (
          <AgendaEmptyState
            isConnected={isConnected}
            hasAdherents={hasAdherents}
            onAction={handleAgendaClick}
          />
        )}

        {!loading && hasEvents && (
          <div className="grid gap-5 lg:grid-cols-3">
            {events.map((event, index) => (
              <EventCard
                key={
                  event?.id ||
                  `${event?.titre}-${getEventStart(event)}-${index}`
                }
                event={event}
                index={index}
                onDetails={setSelectedEvent}
              />
            ))}
          </div>
        )}
      </div>

      <EventDetailsModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onAgendaClick={handleAgendaClick}
      />
    </section>
  );
}

export default AgendaHome;